import { useCallback, useEffect, useMemo } from 'react';

import { usePendingFilterState } from '../../hooks';

import {
  defaultCapacityBounds,
  appliedToPanelState,
  emptyVolumeFilterPanelState,
  withCapacityRange,
  type AppliedVolumeFilters,
  type CapacityBounds,
} from '../panelState';
import { useVolumeFilterUrlParams } from './useVolumeFilterUrlParams';

interface UseVolumeFilterStateProps extends AppliedVolumeFilters {
  open: boolean;
  /** Endpoints for the capacity slider. See `useVolumeCapacityBounds`. */
  capacityBounds?: CapacityBounds;
}

export function useVolumeFilterState({
  open,
  capacityBounds = defaultCapacityBounds,
  ...applied
}: UseVolumeFilterStateProps) {
  const { applyFilters } = useVolumeFilterUrlParams();

  const {
    appliedAutoscaleState,
    appliedEligibility,
    appliedAccountFilters,
    appliedRegionFilters,
    appliedInfrastructureFilters,
    appliedClusterNameFilters,
    appliedNodeGroupFilters,
    appliedInstanceFilters,
    appliedVolumeIdFilters,
    appliedVolumeTagFilters,
    appliedDiskTypeFilters,
    appliedCapacityMin,
    appliedCapacityMax,
  } = applied;

  const boundsMin = capacityBounds.min;
  const boundsMax = capacityBounds.max;

  const getApplied = useCallback(
    () =>
      appliedToPanelState(
        {
          appliedAutoscaleState,
          appliedEligibility,
          appliedAccountFilters,
          appliedRegionFilters,
          appliedInfrastructureFilters,
          appliedClusterNameFilters,
          appliedNodeGroupFilters,
          appliedInstanceFilters,
          appliedVolumeIdFilters,
          appliedVolumeTagFilters,
          appliedDiskTypeFilters,
          appliedCapacityMin,
          appliedCapacityMax,
        },
        // Depended on by value, not identity: `useVolumeCapacityBounds` returns a fresh object
        // every render, which would otherwise resync pending state on every keystroke.
        { min: boundsMin, max: boundsMax }
      ),
    [
      appliedAutoscaleState,
      appliedEligibility,
      appliedAccountFilters,
      appliedRegionFilters,
      appliedInfrastructureFilters,
      appliedClusterNameFilters,
      appliedNodeGroupFilters,
      appliedInstanceFilters,
      appliedVolumeIdFilters,
      appliedVolumeTagFilters,
      appliedDiskTypeFilters,
      appliedCapacityMin,
      appliedCapacityMax,
      boundsMin,
      boundsMax,
    ]
  );

  // Unlike exploration, fleet seeds pending state from the APPLIED filters at mount, not from
  // an empty state — hence `initial` and `buildEmpty` being separate inputs to the hook.
  const initial = useMemo(() => getApplied(), [getApplied]);

  const buildEmpty = useCallback(
    () => emptyVolumeFilterPanelState({ min: boundsMin, max: boundsMax }),
    [boundsMin, boundsMax]
  );

  const { pending, setPending, setField, resetPending } = usePendingFilterState({
    open,
    initial,
    getApplied,
    buildEmpty,
  });

  // The real ceiling can land AFTER the panel has opened — `useVolumeCapacityBounds` is gated
  // on `open` for Reports, so its request only starts on the open edge, and
  // `usePendingFilterState` re-seeds on that edge alone. While capacity is untouched the
  // handles simply track the bounds, so late-arriving bounds are safe to fold in; a range the
  // user picked, or one seeded from the URL, is left exactly as it is.
  useEffect(() => {
    setPending((prev) =>
      prev.capacityFiltered ||
        (prev.capacityBounds.min === boundsMin && prev.capacityBounds.max === boundsMax)
        ? prev
        : {
          ...prev,
          capacityMin: boundsMin,
          capacityMax: boundsMax,
          capacityBounds: { min: boundsMin, max: boundsMax },
        }
    );
  }, [boundsMin, boundsMax, setPending]);

  // Capacity gets dedicated setters rather than `setField`, because moving a handle has to
  // re-derive `capacityFiltered` alongside the value. Both go through the functional form of
  // `setPending` so a drag — which fires far faster than React commits — always reads the
  // other handle from the latest state rather than from this render's closure.
  const setCapacityMin = useCallback(
    (min: number) => setPending((prev) => withCapacityRange(prev, { min })),
    [setPending]
  );

  const setCapacityMax = useCallback(
    (max: number) => setPending((prev) => withCapacityRange(prev, { max })),
    [setPending]
  );

  const clearCapacity = useCallback(
    () =>
      setPending((prev) =>
        withCapacityRange(prev, { min: prev.capacityBounds.min, max: prev.capacityBounds.max })
      ),
    [setPending]
  );

  const applyPendingFilters = useCallback(() => {
    applyFilters(pending);
  }, [applyFilters, pending]);

  return {
    pending,
    setField,
    setCapacityMin,
    setCapacityMax,
    clearCapacity,
    resetPendingFilters: resetPending,
    applyPendingFilters,
  };
}
